import { Braces } from 'lucide-react'
import { MergeTagPicker } from 'tailwind-v4/components/merge-tag-picker'
import { cn } from 'tailwind-v4/lib/utils'
import { INBOX_MERGE_TAGS } from './merge-tags'

interface PassedProps {
  /** Receives the chosen tag's token, e.g. "{{first_name}}". */
  onInsert: (token: string) => void
  /**
   * When provided (e.g. the composer, where the thread/contact is known),
   * the chosen tag is resolved to a real value via `resolve(tag.id)` and that
   * value is inserted instead of the `{{token}}`. Omit for templates, which
   * have no specific recipient and keep the brace placeholder for the backend.
   */
  resolve?: (tagId: string) => string
  /**
   * Extra classes for the trigger. The composer passes row classes when this
   * sits inside its mobile `⋯` popover instead of the editor toolbar.
   */
  className?: string
}

/**
 * Editor-toolbar badge that opens the merge-tag picker and inserts the chosen
 * token (or its resolved value, when a `resolve` fn is supplied) at the cursor.
 */
export function MergeTagInsertButton({
  onInsert,
  resolve,
  className,
}: PassedProps) {
  return (
    <MergeTagPicker
      registry={INBOX_MERGE_TAGS}
      onPick={(tag) => {
        onInsert(resolve ? resolve(tag.id) : tag.value)
      }}
    >
      <button
        type="button"
        title="Insert merge tag"
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-colors',
          // Neutral, like every other button in this toolbar. It used to be a
          // blue pill sitting beside a violet and an emerald one — three raw
          // accents in one row, against a design law that rations the product
          // to a single one. The icon + label carry the meaning.
          'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
          className
        )}
      >
        <Braces className="size-3" />
        Merge tag
      </button>
    </MergeTagPicker>
  )
}

export default MergeTagInsertButton
